import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import {
  getGlobalUsage,
  getGlobalAIUsage,
  GlobalUsageStats,
  GlobalAIGenerationUsageStats,
  AIGeneratorType,
} from "../../api/organization";

interface GlobalUsageDialogProps {
  open: boolean;
  onClose: () => void;
}

const GENERATOR_LABELS: Record<AIGeneratorType, string> = {
  TITLE_GENERATION: "Title",
  COLUMN_DESCRIPTION_GENERATION: "Column Description",
  SHORT_DESCRIPTION_GENERATION: "Short Description",
  LONG_DESCRIPTION_GENERATION: "Long Description",
  REGULAR_DESCRIPTION_GENERATION: "Regular Description",
};

export const GlobalUsageDialog: React.FC<GlobalUsageDialogProps> = ({
  open,
  onClose,
}) => {
  const [tabValue, setTabValue] = useState(0);
  const [translationStats, setTranslationStats] =
    useState<GlobalUsageStats | null>(null);
  const [aiStats, setAIStats] = useState<GlobalAIGenerationUsageStats | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [expandedMonth, setExpandedMonth] = useState<string | false>(false);

  const fetchUsage = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const [translationResponse, aiResponse] = await Promise.all([
        getGlobalUsage(),
        getGlobalAIUsage(),
      ]);
      setTranslationStats(translationResponse?.data?.data || null);
      setAIStats(aiResponse?.data?.data || null);
      // Auto-expand the first month
      if (translationResponse?.data?.data?.monthly?.length > 0) {
        setExpandedMonth(translationResponse.data.data.monthly[0].month);
      }
    } catch (error) {
      console.error("Error fetching global usage:", error);
    } finally {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const formatNumber = (n: number) => n.toLocaleString();
  const formatCost = (n: number) => `$${n.toFixed(4)}`;

  const handleAccordionChange =
    (month: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedMonth(isExpanded ? month : false);
    };

  const hasTranslationData =
    translationStats && translationStats.monthly.length > 0;
  const hasAIData = aiStats && aiStats.monthly.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6">Global Usage Statistics</Typography>
            {tabValue === 0 && translationStats && (
              <Chip
                label={`Total: ${formatNumber(
                  translationStats.grand_total
                )} chars`}
                color="secondary"
                size="small"
              />
            )}
            {tabValue === 1 && aiStats && (
              <>
                <Chip
                  label={`Total: ${formatCost(aiStats.grand_total_cost_usd)}`}
                  color="secondary"
                  size="small"
                />
                <Chip
                  label={`${formatNumber(aiStats.grand_total_generations)} gen`}
                  variant="outlined"
                  size="small"
                />
              </>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs
              value={tabValue}
              onChange={(_, newValue) => setTabValue(newValue)}
              sx={{ mb: 2 }}
            >
              <Tab label="AI Generation" />
              <Tab label="Translation" />
            </Tabs>

            {tabValue === 0 && (
              <>
                {!hasAIData ? (
                  <Typography
                    color="text.secondary"
                    sx={{ py: 4, textAlign: "center" }}
                  >
                    No AI generation usage data yet
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    {aiStats.monthly.map((monthData) => (
                      <Accordion
                        key={monthData.month}
                        expanded={expandedMonth === monthData.month}
                        onChange={handleAccordionChange(monthData.month)}
                        sx={{ mb: 1 }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              pr: 2,
                              gap: 2,
                            }}
                          >
                            <Typography fontWeight="medium">
                              {monthData.month}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Chip
                                label={formatCost(monthData.total_cost_usd)}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                label={`${formatNumber(
                                  monthData.total_generations
                                )} gen`}
                                size="small"
                                variant="outlined"
                              />
                            </Box>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {monthData.organizations.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                              No organization usage this month
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {monthData.organizations.map((org) => (
                                <Box
                                  key={org.id}
                                  sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 2,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight="medium"
                                    >
                                      {org.name}
                                    </Typography>
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                      <Chip
                                        label={formatCost(org.total_cost_usd)}
                                        size="small"
                                        color="primary"
                                      />
                                      <Chip
                                        label={`${formatNumber(
                                          org.total_generations
                                        )} gen`}
                                        size="small"
                                        variant="outlined"
                                      />
                                    </Box>
                                  </Box>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableHead>
                                        <TableRow>
                                          <TableCell>Generator</TableCell>
                                          <TableCell>Model</TableCell>
                                          <TableCell align="right">
                                            Cost
                                          </TableCell>
                                          <TableCell align="right">
                                            Count
                                          </TableCell>
                                        </TableRow>
                                      </TableHead>
                                      <TableBody>
                                        {Object.entries(
                                          org.by_generator
                                        ).flatMap(
                                          ([generator, genData]) =>
                                            genData?.models.map((model) => (
                                              <TableRow
                                                key={`${generator}-${model.name}`}
                                              >
                                                <TableCell>
                                                  {GENERATOR_LABELS[
                                                    generator as AIGeneratorType
                                                  ] || generator}
                                                </TableCell>
                                                <TableCell>
                                                  {model.name}
                                                </TableCell>
                                                <TableCell align="right">
                                                  {formatCost(model.cost_usd)}
                                                </TableCell>
                                                <TableCell align="right">
                                                  {formatNumber(
                                                    model.generations
                                                  )}
                                                </TableCell>
                                              </TableRow>
                                            )) || []
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
              </>
            )}

            {tabValue === 1 && (
              <>
                {!hasTranslationData ? (
                  <Typography
                    color="text.secondary"
                    sx={{ py: 4, textAlign: "center" }}
                  >
                    No translation usage data yet
                  </Typography>
                ) : (
                  <Box sx={{ mt: 1 }}>
                    {translationStats.monthly.map((monthData) => (
                      <Accordion
                        key={monthData.month}
                        expanded={expandedMonth === monthData.month}
                        onChange={handleAccordionChange(monthData.month)}
                        sx={{ mb: 1 }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                              pr: 2,
                            }}
                          >
                            <Typography fontWeight="medium">
                              {monthData.month}
                            </Typography>
                            <Chip
                              label={`${formatNumber(monthData.total)} chars`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          {monthData.organizations.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                              No organization usage this month
                            </Typography>
                          ) : (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                              }}
                            >
                              {monthData.organizations.map((org) => (
                                <Box
                                  key={org.id}
                                  sx={{
                                    p: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                    borderRadius: 1,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight="medium"
                                    >
                                      {org.name}
                                    </Typography>
                                    <Chip
                                      label={`${formatNumber(org.total)} chars`}
                                      size="small"
                                      color="primary"
                                    />
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
